/**
 * The window of Slack read around one meeting (owner ruling 20260926-002133, D11): from this many
 * days before the meeting starts to this many days after it ends, never past the moment of the check.
 * Why here: the Connections page and Settings state the window to the person before they connect,
 * and retrieval reads exactly what they were told, from these two numbers.
 */
export const SLACK_WINDOW_DAYS_BEFORE = 3;
export const SLACK_WINDOW_DAYS_AFTER = 7;

/**
 * Before the window, a check reads one more page of history, over this many days, in its busiest
 * sources — only to find threads still going on in the window, and it keeps just their first
 * messages (ADR 0009 §1). Why here: the Connections page and the "Look in Slack" dialog say so.
 */
export const SLACK_LOOKBACK_DAYS = 14;

/**
 * A related thread is sent with its first message and its latest replies, at most this many, whatever
 * they say — how a thread ended decides between a follow-up and mere context (ADR 0009 §2). Stated to
 * the person as well.
 */
export const SLACK_THREAD_LATEST_REPLIES = 3;

/**
 * Where the Connections page sends the browser to connect Slack: a server landing (a full
 * navigation, never a fetch), which ends back on `/connections?slack=<outcome>`.
 */
export const SLACK_CONNECT_PATH = '/auth/slack/start';

/**
 * A finding's id on the meeting's Slack tab: `f` and the first sixteen hex characters of its fingerprint
 * (a second finding of one check with the same sixteen gets `-2`, `-3`…). Why here: the server names a
 * finding by this rule, the page addresses it (`?tab=slack#s-<id>`), and a to-do or a note made from it
 * points back at it by the same id — one rule, stated once.
 */
export const SLACK_FINDING_ID_PATTERN = /^f[0-9a-f]{16}(?:-\d{1,3})?$/u;

export function slackFindingIdOf(fingerprint: string): string {
  return `f${fingerprint.slice(0, 16)}`;
}

export function isSlackFindingId(value: string): boolean {
  return SLACK_FINDING_ID_PATTERN.test(value);
}

/**
 * An item's id in the daily Slack sweep's sealed record: a letter naming what it is — `m` a matter, `u` an update on
 * an open item of the brief, `n` a note on a to-do, `d` a sign that a to-do was done — and sixteen hex characters of
 * its fingerprint (a second matter of one sweep with the same sixteen gets `-2`, `-3`…). Why here: the server mints
 * them (ai/slack-sweep/validate.ts), a sweep's to-do and note keep them as what they rest on, and the page addresses
 * them on the Assistant's "Slack today" tab — one rule the store and the page share, as for a check's finding.
 */
export const SLACK_SWEEP_ITEM_ID_PATTERN = /^[mund][0-9a-f]{16}(?:-\d{1,3})?$/u;

export function isSlackSweepItemId(value: string): boolean {
  return SLACK_SWEEP_ITEM_ID_PATTERN.test(value);
}

/**
 * The daily Slack sweep's timing, in the owner's own numbers (rulings of 2026-09-30). Why constants and not
 * configuration: the owner fixed each one, and the Connections page states them to the person, so a setting
 * that could differ from what the page says would make the page untrue.
 * - Q3: a person's next sweep is due this many hours after their last one read up to, whatever time of day
 *   that falls (no fixed hour).
 * - N2: the first sweep reads this many hours back…
 * - …and a sweep after a gap (downtime, failures, the switch off for a while) reads at most this many hours
 *   back; the older days are skipped, and the sweep records from when.
 * - N4: sweeping pauses for a person who has not opened Calis for this many days, and resumes on their next
 *   visit.
 */
export const SLACK_SWEEP_INTERVAL_HOURS = 24;
export const SLACK_SWEEP_FIRST_HOURS = 24;
export const SLACK_SWEEP_MAX_GAP_HOURS = 72;
export const SLACK_SWEEP_PAUSE_DAYS = 14;

/**
 * At most this many to-dos the daily Slack sweep may add for one person in any 24 hours (owner ruling Q5 of
 * 2026-09-30: "at most 5 per person per day"). Why a constant and not configuration: the owner fixed the number and
 * the Connections page states it to the person, so a setting that could differ would make the page untrue. Shared so
 * the page states the number the store keeps to (stores/todos.ts addSweepTodosForOwner).
 */
export const SWEEP_TODOS_PER_DAY = 5;
